/* eslint-disable @typescript-eslint/no-explicit-any */
import { ENDPOINTS } from '@/util'
import { useSuspenseQuery } from '@tanstack/react-query'
import { useGetFilterSelections } from '@/catalog-apps/hooks/useGetFilterSelections'
import { MdTextResponse } from './types'
import { useAppContext } from '@/catalog-apps/hooks/useAppContext'
import { useGetPageFilters } from '../../AppPage2/Filter/useGetPageFilters'
import { useIsPageBuilder } from '@/catalog-apps/hooks/useIsPageBuilder'
import { TComponentType } from '../../AppPage2/types'

const USE_MOCK_DATA = false

interface GetMdTextParams {
  appKey?: string
  contentId?: string
  projectId?: number
  filterSelections: Record<string, any>[]
  isPageBuilder?: boolean
  payload?: Record<string, any>
}
const getMdText = async ({
  appKey,
  contentId,
  projectId,
  filterSelections,
  isPageBuilder,
  payload,
}: GetMdTextParams) => {
  if (!appKey || !contentId || !projectId) {
    throw new Error('Missing required parameters')
  }
  if (isPageBuilder || USE_MOCK_DATA) {
    return Promise.resolve({
      ...(await import('@/catalog-apps/mock-data/md-text/md-text-response-2.json')).json,
    } as MdTextResponse)
  }
  const token = JSON.parse(sessionStorage.getItem('token') || '')
  if (!token) {
    throw new Error('No token found')
  }
  const url = `${ENDPOINTS.CC_DOTNET}/content`
  const body = {
    app_id: appKey,
    content_id: contentId,
    project_id: projectId,
    filters: filterSelections,
  }
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(payload ? { ...body, payload } : body),
  })
  const data = (await response.json()) as MdTextResponse
  if (response.ok) {
    return data
  }
  throw new Error('Failed to fetch form')
}

export function useGetMdText(
  contentId: string,
  tileFilterSelections: Record<string, any>[],
  payload?: Record<string, any>,
  initialData?: MdTextResponse
) {
  const isPageBuilder = useIsPageBuilder()
  const {
    data: { defaultSelections },
  } = useGetPageFilters()
  const { filterSelections } = useGetFilterSelections()
  const { appKey, projectId } = useAppContext()
  const filterSelectionsArray =
    filterSelections.length > 0
      ? [...filterSelections, ...tileFilterSelections]
      : [...defaultSelections, ...tileFilterSelections]
  const componentType: TComponentType = 'md-text'
  return useSuspenseQuery({
    queryKey: [contentId, componentType, appKey, projectId, filterSelectionsArray, isPageBuilder, payload],
    queryFn: () =>
      getMdText({
        appKey,
        contentId,
        projectId,
        filterSelections: filterSelectionsArray,
        isPageBuilder,
        payload,
      }),
    initialData,
    staleTime: initialData ? Number.POSITIVE_INFINITY : 0,
  })
}
